import {
  useState,
  useRef,
  useEffect,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import ReactMarkdown from "react-markdown";

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled: boolean;
  primaryColor: string;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  footerText?: string | null;
  disableSendMessage?: boolean;
}

export function MessageInput({
  onSendMessage,
  disabled,
  primaryColor,
  textareaRef: externalTextareaRef,
  footerText,
  disableSendMessage,
}: MessageInputProps) {
  const [message, setMessage] = useState("");
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = externalTextareaRef || internalTextareaRef;

  useEffect(() => {
    if (textareaRef.current) {
      if (message.trim() === "") {
        textareaRef.current.style.height = "32px";
      } else {
        textareaRef.current.style.height = "0px";
        const scrollHeight = textareaRef.current.scrollHeight;
        textareaRef.current.style.height = `${Math.max(32, scrollHeight)}px`;
      }
    }
  }, [message]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled && !disableSendMessage) {
      onSendMessage(message);
      setMessage("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="shrink-0 border-t border-[#E0E0E0] bg-white px-6 pb-6 pt-4"
    >
      <div className="flex items-center gap-3">
        <label htmlFor="message-input" className="sr-only">
          Type your message
        </label>
        <textarea
          ref={textareaRef}
          id="message-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
          disabled={disabled || disableSendMessage}
          rows={1}
          className="min-h-8 max-h-32 flex-1 resize-none overflow-y-hidden rounded border border-transparent bg-transparent py-1 text-base leading-tight text-black placeholder:text-[#5A5A5A] focus-visible:border-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Message input"
          aria-required="true"
        />
        <button
          type="submit"
          disabled={disabled || !message.trim() || disableSendMessage}
          aria-label="Send message"
          style={{
            backgroundColor:
              disabled || !message.trim() || disableSendMessage
                ? `${primaryColor}99`
                : primaryColor,
          }}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-white transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed"
          onMouseEnter={(e) => {
            if (!disabled && message.trim() && !disableSendMessage) {
              e.currentTarget.style.backgroundColor = `${primaryColor}E6`;
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled && message.trim() && !disableSendMessage) {
              e.currentTarget.style.backgroundColor = primaryColor;
            }
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path
              d="M8 12L8 4M8 4L4 8M8 4L12 8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      {footerText && (
        <div className="mt-2 text-xs text-[#5A5A5A]">
          <ReactMarkdown
            components={{
              p: ({ node, ...props }) => <p className="m-0" {...props} />,
              a: ({ node, ...props }) => (
                <a
                  {...props}
                  className="text-blue-600 underline hover:text-blue-800"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          >
            {footerText}
          </ReactMarkdown>
        </div>
      )}
    </form>
  );
}
